<template>
  <div class="membership-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">会员权益管理</h2>
        <p class="page-subtitle">配置和管理会员等级、价格策略及专属权益</p>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="handleCreateLevel">
          <el-icon><Plus /></el-icon>
          新增会员等级
        </el-button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="8" animated />
    </div>

    <!-- 空状态 -->
    <div v-else-if="membershipLevels.length === 0" class="empty-container">
      <el-empty description="暂无会员等级">
        <el-button type="primary" @click="handleCreateLevel">
          <el-icon><Plus /></el-icon>
          创建第一个会员等级
        </el-button>
      </el-empty>
    </div>

    <!-- 会员等级列表 -->
    <div v-else class="level-list">
      <el-row :gutter="24">
        <el-col 
          :xs="24" 
          :sm="24" 
          :md="12" 
          :lg="8"
          :xl="8"
          v-for="level in membershipLevels" 
          :key="level.id"
        >
          <el-card shadow="hover" class="level-card" :class="{ 'ticket-card': isTicketLevel(level) }">
            <template #header>
              <div class="card-header">
                <div class="level-info">
                  <h3 class="level-name">{{ level.name }}</h3>
                  <el-tag 
                    :type="level.status === 'active' ? 'success' : 'info'" 
                    size="small"
                  >
                    {{ level.status === 'active' ? '启用' : '禁用' }}
                  </el-tag>
                  <el-tag v-if="isTicketLevel(level)" type="warning" size="small" effect="plain">
                    按次购买
                  </el-tag>
                </div>
                <div class="card-actions">
                  <el-dropdown @command="(cmd) => handleLevelAction(cmd, level)">
                    <el-icon class="more-icon"><MoreFilled /></el-icon>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item command="edit">
                          <el-icon><Edit /></el-icon>
                          编辑
                        </el-dropdown-item>
                        <el-dropdown-item command="toggle">
                          <el-icon><Switch /></el-icon>
                          {{ level.status === 'active' ? '禁用' : '启用' }}
                        </el-dropdown-item>
                        <el-dropdown-item command="delete" divided>
                          <el-icon><Delete /></el-icon>
                          删除
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </div>
            </template>

            <div class="level-content">
              <!-- 价格配置 -->
              <div class="price-section">
                <h4>价格配置</h4>
                <div v-if="level.prices && level.prices.length > 0" class="price-list">
                  <div 
                    v-for="price in level.prices" 
                    :key="price.months"
                    class="price-item"
                  >
                    <span class="duration">{{ isTicketLevel(level) ? '单次' : price.months + '个月' }}</span>
                    <span class="price">{{ price.price === 0 ? '免费' : '¥' + price.price }}</span>
                  </div>
                </div>
                <div v-else class="empty-tip">
                  <el-text type="info" size="small">免费</el-text>
                </div>
              </div>

              <!-- 权益列表 -->
              <div class="benefits-section">
                <h4>专属权益</h4>
                <ul class="benefits-list">
                  <li v-for="(benefit, index) in level.benefits" :key="index">
                    <el-icon color="#67c23a"><Check /></el-icon>
                    {{ benefit }}
                  </li>
                </ul>
              </div>

              <!-- 限制说明 -->
              <div class="limits-section" v-if="level.limits && !isTicketLevel(level)">
                <h4>使用限制</h4>
                <div class="limit-item">
                  <el-icon><Calendar /></el-icon>
                  每月最多创建 <strong>{{ level.limits.max_activities }}</strong> 个活动
                </div>
              </div>

              <!-- 单次发起券说明 -->
              <div class="limits-section" v-if="isTicketLevel(level)">
                <h4>使用规则</h4>
                <div class="limit-item ticket-limit">
                  <el-icon><Ticket /></el-icon>
                  <div class="ticket-limit-text">
                    <div>每次购买获得 <strong>1</strong> 次发起活动机会</div>
                    <div class="ticket-limit-sub">按次使用，用完即止</div>
                  </div>
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 创建/编辑会员等级对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogMode === 'create' ? '新增会员等级' : (isTicketForm ? '编辑单次发起券' : '编辑会员等级')"
      width="700px"
      @close="handleDialogClose"
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-width="100px"
      >
        <el-form-item label="等级名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入会员等级名称" />
        </el-form-item>

        <el-form-item label="等级代码" prop="code">
          <el-input 
            v-model="formData.code" 
            placeholder="请输入等级代码（如：premium, vip）"
            :disabled="dialogMode === 'edit'"
          />
          <div class="form-tip">
            {{ isTicketForm ? '单次发起券为系统内置权益，代码固定为 single_ticket' : '等级代码用于系统内部标识，创建后不可修改' }}
          </div>
        </el-form-item>

        <el-form-item label="等级排序" prop="sort_order">
          <el-input-number 
            v-model="formData.sort_order" 
            :min="0" 
            :max="100"
            placeholder="数值越小越靠前"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-switch 
            v-model="formData.status" 
            active-value="active"
            inactive-value="inactive"
            active-text="启用" 
            inactive-text="禁用"
          />
        </el-form-item>

        <el-form-item label="推荐等级">
          <el-switch 
            v-model="formData.is_recommended" 
            :active-value="1"
            :inactive-value="0"
          />
          <div class="form-tip">推荐等级会在前端显著展示</div>
        </el-form-item>

        <el-divider>价格配置</el-divider>

        <el-form-item label="价格策略">
          <div class="price-config">
            <div 
              v-for="(price, index) in formData.prices" 
              :key="index"
              class="price-config-item"
            >
              <template v-if="isTicketForm">
                <span class="separator">单次价格</span>
              </template>
              <template v-else>
                <el-input-number 
                  v-model="price.months" 
                  :min="1" 
                  :max="36"
                  placeholder="月数"
                  style="width: 100px"
                />
                <span class="separator">个月</span>
              </template>
              <el-input-number 
                v-model="price.price" 
                :min="0" 
                :precision="2"
                placeholder="价格"
                style="width: 120px"
              />
              <span class="separator">元（设为0表示免费）</span>
              <el-button 
                type="danger" 
                :icon="Delete" 
                circle 
                size="small"
                @click="removePriceItem(index)"
                :disabled="formData.prices.length === 1"
              />
            </div>
            <el-button 
              v-if="!isTicketForm"
              type="primary" 
              :icon="Plus" 
              plain 
              size="small"
              @click="addPriceItem"
            >
              添加价格档位
            </el-button>
            <div class="form-tip price-sync-tip">
              修改价格后，请同步在微信公众平台「虚拟支付 → 道具管理」中把对应道具的价格改为相同金额，否则用户拉起支付时会因价格不一致而失败。
              <template v-if="isTicketForm">单次发起券对应道具ID：<strong>dancifaqiquan</strong>。</template>
            </div>
          </div>
        </el-form-item>

        <el-divider>权益配置</el-divider>

        <el-form-item label="专属权益">
          <div class="benefits-config">
            <div 
              v-for="(benefit, index) in formData.benefits" 
              :key="index"
              class="benefit-item"
            >
              <el-input 
                v-model="formData.benefits[index]" 
                placeholder="请输入权益描述"
              />
              <el-button 
                type="danger" 
                :icon="Delete" 
                circle 
                size="small"
                @click="removeBenefit(index)"
                :disabled="formData.benefits.length === 1"
              />
            </div>
            <el-button 
              type="primary" 
              :icon="Plus" 
              plain 
              size="small"
              @click="addBenefit"
            >
              添加权益
            </el-button>
          </div>
        </el-form-item>

        <template v-if="!isTicketForm">
          <el-divider>使用限制</el-divider>

          <el-form-item label="活动创建">
            <el-input-number 
              v-model="formData.max_activities" 
              :min="0" 
              :max="999"
              placeholder="每月最多创建活动数量"
            />
            <span class="form-tip">个/月，0表示不限制</span>
          </el-form-item>

          <el-form-item label="其他限制">
            <el-input 
              v-model="formData.other_limits" 
              type="textarea"
              :rows="3"
              placeholder="其他使用限制说明（可选）"
            />
          </el-form-item>
        </template>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          确定
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Edit, Delete, MoreFilled, Check, Calendar, Switch, Ticket } from '@element-plus/icons-vue'
import api from '@/utils/api'

export default {
  name: 'MembershipManagement',
  
  components: {
    Plus,
    Edit,
    Delete,
    MoreFilled,
    Check,
    Calendar,
    Switch,
    Ticket
  },
  
  setup() {
    const loading = ref(true)
    const submitting = ref(false)
    const membershipLevels = ref([])
    const dialogVisible = ref(false)
    const dialogMode = ref('create')
    const formRef = ref(null)
    
    // 表单数据
    const formData = reactive({
      id: null,
      name: '',
      code: '',
      sort_order: 0,
      status: 'active',
      is_recommended: 0,
      prices: [
        { months: 1, price: 30 }
      ],
      benefits: ['创建活动权限'],
      max_activities: 5,
      other_limits: ''
    })
    
    // 表单验证规则
    const formRules = {
      name: [
        { required: true, message: '请输入等级名称', trigger: 'blur' },
        { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
      ],
      code: [
        { required: true, message: '请输入等级代码', trigger: 'blur' },
        { pattern: /^[a-z_]+$/, message: '只能包含小写字母和下划线', trigger: 'blur' }
      ],
      sort_order: [
        { required: true, message: '请输入排序值', trigger: 'blur' }
      ]
    }

    // 单次发起券：与会员等级并列展示，但按「次」售卖，不参与每月活动配额
    const TICKET_CODE = 'single_ticket'
    const isTicketLevel = (level) => !!level && level.code === TICKET_CODE
    const isTicketForm = computed(() => formData.code === TICKET_CODE)
    
    // 加载会员等级列表
    const loadMembershipLevels = async () => {
      loading.value = true
      
      console.log('\n========================================')
      console.log('🔄 [前端] 开始加载会员等级数据')
      console.log('时间:', new Date().toLocaleString('zh-CN'))
      console.log('========================================')
      
      try {
        console.log('📡 正在调用 API: getMembershipLevels()')
        const response = await api.getMembershipLevels()
        
        console.log('\n✅ API响应数据:')
        console.log('  - success:', response.success)
        console.log('  - 数据类型:', Array.isArray(response.data) ? 'Array' : typeof response.data)
        console.log('  - 记录数量:', response.data?.length || 0)
        
        if (response.data && response.data.length > 0) {
          console.log('\n📋 会员等级详情:')
          response.data.forEach((level, index) => {
            console.log(`  第${index + 1}条: ${level.name} (${level.code})`)
            console.log(`    - ID: ${level.id}`)
            console.log(`    - 状态: ${level.status}`)
            console.log(`    - 价格档位: ${level.prices?.length || 0} 个`)
            if (level.prices && level.prices.length > 0) {
              console.log(`    - 首个价格: ¥${level.prices[0].price}/${level.prices[0].months}个月`)
            }
            console.log(`    - 权益数量: ${level.benefits?.length || 0} 项`)
            if (level.benefits && level.benefits.length > 0) {
              level.benefits.forEach((b, i) => {
                console.log(`      ${i + 1}. ${b}`)
              })
            }
          })
        }
        
        if (response.success) {
          membershipLevels.value = response.data || []
          console.log('\n✅ 数据已存储到 membershipLevels 变量')
          console.log('   当前 membershipLevels 长度:', membershipLevels.value.length)
          
          if (membershipLevels.value.length === 0) {
            console.log('⚠️  数据为空，显示空状态提示')
            ElMessage.warning('暂无会员等级数据，请先创建会员等级')
          } else {
            console.log('✅ 页面将渲染', membershipLevels.value.length, '个会员等级卡片')
          }
        } else {
          console.error('❌ API返回失败:', response.message)
          ElMessage.error(response.message || '获取会员等级失败')
        }
        
        console.log('========================================')
        console.log('✅ 加载流程完成')
        console.log('========================================\n')
        
      } catch (error) {
        console.error('\n❌ 加载会员等级失败 - 异常捕获:')
        console.error('  错误类型:', error.name)
        console.error('  错误信息:', error.message)
        console.error('  完整错误:', error)
        
        if (error.response) {
          console.error('  HTTP状态:', error.response.status)
          console.error('  响应数据:', error.response.data)
        }
        console.log('========================================\n')
        
        ElMessage.error(error.response?.data?.message || '加载会员等级失败，请检查网络连接')
      } finally {
        loading.value = false
        console.log('🔚 loading状态已设置为 false')
      }
    }
    
    // 新增会员等级
    const handleCreateLevel = () => {
      dialogMode.value = 'create'
      resetForm()
      dialogVisible.value = true
    }
    
    // 处理等级操作
    const handleLevelAction = (command, level) => {
      switch (command) {
        case 'edit':
          handleEditLevel(level)
          break
        case 'toggle':
          handleToggleStatus(level)
          break
        case 'delete':
          handleDeleteLevel(level)
          break
      }
    }
    
    // 编辑会员等级
    const handleEditLevel = (level) => {
      dialogMode.value = 'edit'
      formData.id = level.id
      formData.name = level.name
      formData.code = level.code
      formData.sort_order = level.sort_order
      formData.status = level.status
      formData.is_recommended = level.is_recommended
      formData.prices = JSON.parse(JSON.stringify(level.prices))
      formData.benefits = JSON.parse(JSON.stringify(level.benefits))
      formData.max_activities = level.limits?.max_activities || 0
      formData.other_limits = level.limits?.other || ''
      dialogVisible.value = true
    }
    
    // 切换状态
    const handleToggleStatus = async (level) => {
      try {
        const newStatus = level.status === 'active' ? 'inactive' : 'active'
        const statusText = newStatus === 'active' ? '启用' : '禁用'
        
        const response = await api.updateMembershipLevel(level.id, { 
          name: level.name,
          sort_order: level.sort_order,
          status: newStatus,
          is_recommended: level.is_recommended,
          prices: level.prices,
          benefits: level.benefits,
          limits: level.limits
        })
        
        if (response.success) {
          level.status = newStatus
          ElMessage.success(`${statusText}成功`)
        } else {
          ElMessage.error(response.message || `${statusText}失败`)
        }
      } catch (error) {
        console.error('更新状态失败:', error)
        ElMessage.error(error.response?.data?.message || '更新状态失败')
      }
    }
    
    // 删除会员等级
    const handleDeleteLevel = async (level) => {
      // 保护普通会员
      if (level.code === 'normal') {
        ElMessage.warning('普通会员等级不能删除')
        return
      }
      // 保护单次发起券（小程序购买链路依赖该配置）
      if (isTicketLevel(level)) {
        ElMessage.warning('单次发起券为系统内置权益，不能删除；如需停售请将其禁用')
        return
      }
      
      try {
        await ElMessageBox.confirm(
          `确定要删除会员等级"${level.name}"吗？删除后无法恢复。`,
          '确认删除',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        const response = await api.deleteMembershipLevel(level.id)
        
        if (response.success) {
          ElMessage.success('删除成功')
          loadMembershipLevels()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error(error.response?.data?.message || '删除失败')
        }
      }
    }
    
    // 添加价格档位
    const addPriceItem = () => {
      formData.prices.push({ months: 1, price: 30 })
    }
    
    // 删除价格档位
    const removePriceItem = (index) => {
      formData.prices.splice(index, 1)
    }
    
    // 添加权益
    const addBenefit = () => {
      formData.benefits.push('')
    }
    
    // 删除权益
    const removeBenefit = (index) => {
      formData.benefits.splice(index, 1)
    }
    
    // 提交表单
    const handleSubmit = async () => {
      try {
        await formRef.value.validate()
        
        // 验证价格配置
        if (formData.prices.length === 0) {
          ElMessage.warning('请至少添加一个价格档位')
          return
        }
        
        // 验证权益配置
        if (formData.benefits.length === 0) {
          ElMessage.warning('请至少添加一个权益')
          return
        }
        
        // 验证权益是否为空
        const emptyBenefits = formData.benefits.filter(b => !b || !b.trim())
        if (emptyBenefits.length > 0) {
          ElMessage.warning('请填写所有权益描述')
          return
        }
        
        submitting.value = true
        
        // 单次发起券只有「单次」一个档位，统一规范为 months=1
        const prices = isTicketForm.value
          ? [{ months: 1, price: Number(formData.prices[0]?.price) || 0 }]
          : formData.prices
        
        const data = {
          name: formData.name,
          code: formData.code,
          sort_order: formData.sort_order,
          status: formData.status,
          is_recommended: formData.is_recommended,
          prices: prices,
          benefits: formData.benefits,
          limits: isTicketForm.value
            ? { max_activities: 1, other: formData.other_limits || '单次有效，不随会员到期清零' }
            : {
                max_activities: formData.max_activities,
                other: formData.other_limits
              }
        }
        
        if (dialogMode.value === 'create') {
          const response = await api.createMembershipLevel(data)
          if (response.success) {
            ElMessage.success('创建成功')
            dialogVisible.value = false
            loadMembershipLevels()
          } else {
            ElMessage.error(response.message || '创建失败')
          }
        } else {
          const response = await api.updateMembershipLevel(formData.id, data)
          if (response.success) {
            ElMessage.success('更新成功')
            dialogVisible.value = false
            loadMembershipLevels()
          } else {
            ElMessage.error(response.message || '更新失败')
          }
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('提交失败:', error)
          ElMessage.error(error.response?.data?.message || '提交失败，请重试')
        }
      } finally {
        submitting.value = false
      }
    }
    
    // 重置表单
    const resetForm = () => {
      formData.id = null
      formData.name = ''
      formData.code = ''
      formData.sort_order = 0
      formData.status = 'active'
      formData.is_recommended = 0
      formData.prices = [{ months: 1, price: 30 }]
      formData.benefits = ['创建活动权限']
      formData.max_activities = 5
      formData.other_limits = ''
    }
    
    // 关闭对话框
    const handleDialogClose = () => {
      formRef.value?.resetFields()
    }
    
    onMounted(() => {
      loadMembershipLevels()
    })
    
    return {
      loading,
      submitting,
      membershipLevels,
      dialogVisible,
      dialogMode,
      formRef,
      formData,
      formRules,
      isTicketLevel,
      isTicketForm,
      
      loadMembershipLevels,
      handleCreateLevel,
      handleLevelAction,
      handleEditLevel,
      handleToggleStatus,
      handleDeleteLevel,
      addPriceItem,
      removePriceItem,
      addBenefit,
      removeBenefit,
      handleSubmit,
      handleDialogClose,
      
      Plus,
      Edit,
      Delete
    }
  }
}
</script>

<style scoped>
.membership-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  flex: 1;
}

.page-title {
  margin: 0 0 8px 0;
  color: #303133;
  font-size: 20px;
  font-weight: 600;
}

.page-subtitle {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.loading-container {
  padding: 20px;
  background: #fff;
  border-radius: 4px;
}

.empty-container {
  padding: 60px 20px;
  background: #fff;
  border-radius: 4px;
  text-align: center;
}

.empty-tip {
  text-align: center;
  padding: 8px;
  color: #909399;
  font-size: 12px;
}

.level-list {
  margin-top: 20px;
}

.level-card {
  margin-bottom: 24px;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.level-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

/* 单次发起券卡片：与会员等级卡片同版式，用金色描边区分 */
.ticket-card {
  border: 1px solid #e6a23c;
  box-shadow: 0 4px 16px rgba(230, 162, 60, 0.16);
}

.ticket-card :deep(.el-card__header) {
  background: linear-gradient(135deg, #fdf6ec, #faecd8);
}

.ticket-limit {
  background: linear-gradient(135deg, #f0f9eb, #e1f3d8);
  border-left-color: #67c23a;
  align-items: flex-start;
}

.ticket-limit-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ticket-limit-sub {
  color: #909399;
  font-size: 12px;
}

.ticket-limit strong {
  color: #67c23a;
}

.price-sync-tip {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fdf6ec;
  border-left: 3px solid #e6a23c;
  color: #b88230;
  line-height: 1.6;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 2;
}

.level-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.level-name {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.card-actions {
  position: relative;
  z-index: 10;
}

.more-icon {
  font-size: 20px;
  cursor: pointer;
  color: #909399;
  transition: all 0.3s;
  padding: 4px;
  border-radius: 4px;
}

.more-icon:hover {
  color: #409eff;
  background-color: #f5f7fa;
}

.level-content {
  padding-top: 12px;
}

.price-section,
.benefits-section,
.limits-section {
  margin-bottom: 20px;
}

.price-section h4,
.benefits-section h4,
.limits-section h4 {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.price-section h4::before {
  content: '💰';
  margin-right: 8px;
}

.benefits-section h4::before {
  content: '✨';
  margin-right: 8px;
}

.limits-section h4::before {
  content: '⚠️';
  margin-right: 8px;
}

.price-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.price-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: linear-gradient(135deg, #f5f7fa, #e8ecf1);
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  transition: all 0.3s;
}

.price-item:hover {
  background: linear-gradient(135deg, #ecf5ff, #d9ecff);
  border-color: #409eff;
  transform: scale(1.05);
}

.duration {
  color: #606266;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
}

.price {
  color: #f56c6c;
  font-size: 20px;
  font-weight: 700;
}

.benefits-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.benefits-list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  border-bottom: 1px solid #f0f0f0;
}

.benefits-list li:last-child {
  border-bottom: none;
}

.benefits-list li .el-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.limit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #fff7e6, #ffe7ba);
  border-radius: 8px;
  border-left: 4px solid #e6a23c;
  color: #606266;
  font-size: 14px;
}

.limit-item strong {
  color: #e6a23c;
  font-size: 18px;
  font-weight: 700;
}

.form-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.price-config,
.benefits-config {
  width: 100%;
}

.price-config-item,
.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.separator {
  color: #909399;
  font-size: 14px;
}

.benefit-item .el-input {
  flex: 1;
}
</style>
